// full updated LogsHome.js with multi-delete support
import "./LogsHome.scss";
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faHouse,
  faPlus,
  faChartLine,
  faTable,
  faPenToSquare,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import Select from 'react-select';
import { modeOptions, groupedOptions } from './options.js';
import chroma from 'chroma-js';

const LogsHome = () => {
  const location = useLocation();
  const product = location.state?.product;
  const [LogFileArray, setLogFileArray] = useState([]);
  const [LogFilesLoading, setLogFilesLoading] = useState(true);
  const [EventsLoading, setEventsLoading] = useState(true);
  const [selectedLogs, setSelectedLogs] = useState([]);
  const [expandedLogs, setExpandedLogs] = useState([]);

  const groupStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  };

  const groupBadgeStyles = {
    backgroundColor: '#EBECF0',
    borderRadius: '2em',
    color: '#172B4D',
    display: 'inline-block',
    fontSize: 12,
    fontWeight: 'normal',
    lineHeight: '1',
    minWidth: 1,
    padding: '0.16666666666667em 0.5em',
    textAlign: 'center',
  };

  const dot = (color = 'transparent') => ({
    alignItems: 'center',
    display: 'flex',
    ':before': {
      backgroundColor: color,
      borderRadius: 10,
      content: '" "',
      display: 'block',
      marginRight: 8,
      height: 10,
      width: 10,
    },
  });

  const colorStyles = {
    control: (styles) => ({ ...styles, backgroundColor: 'white' }),
    option: (styles, { data, isDisabled, isFocused, isSelected }) => {
      const color = chroma(data.color);
      return {
        ...styles,
        backgroundColor: isDisabled ? undefined : isSelected ? data.color : isFocused ? color.alpha(0.1).css() : undefined,
        color: isDisabled ? '#ccc' : isSelected ? chroma.contrast(color, 'white') > 2 ? 'white' : 'black' : data.color,
        cursor: isDisabled ? 'not-allowed' : 'default',
        ':active': {
          ...styles[':active'],
          backgroundColor: !isDisabled ? isSelected ? data.color : color.alpha(0.3).css() : undefined,
        },
      };
    },
    multiValue: (styles, { data }) => {
      const color = chroma(data.color);
      return {
        ...styles,
        backgroundColor: color.alpha(0.1).css(),
      };
    },
    multiValueLabel: (styles, { data }) => ({
      ...styles,
      color: data.color,
    }),
    multiValueRemove: (styles, { data }) => ({
      ...styles,
      color: data.color,
      ':hover': {
        backgroundColor: data.color,
        color: 'white',
      },
    }),
  };

  const formatGroupLabel = (data) => (
    <div style={groupStyles}>
      <span>{data.label}</span>
      <span style={groupBadgeStyles}>{data.options.length}</span>
    </div>
  );

  const navigate = useNavigate();

  useEffect(() => {
    fetchLogFiles();
  }, []);

  const fetchLogFiles = async () => {
    try {
      setLogFilesLoading(false);
      const response = await fetch(`https://sucmhxbjlk.execute-api.us-east-1.amazonaws.com/prod/logs/get/${product.id}`);
      if (!response.ok) throw new Error("Failed to fetch log files");

      const jsonBody = await response.json();
      let logFileArray = jsonBody.data.map((logFile) => ({
        ...logFile,
        rangeLoading: true,
        eventsLoading: true,
        rangeStartDate: "",
        rangeStartTime: "",
        rangeEndDate: "",
        rangeEndTime: "",
        eventsData: [],
      }));

      logFileArray.sort((a, b) => a.log_filename.localeCompare(b.log_filename));
      setLogFileArray(logFileArray);
      await fetchEvents();
      setLogFilesLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLogFilesLoading(true);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await fetch(`https://sucmhxbjlk.execute-api.us-east-1.amazonaws.com/prod/product/events`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (response.ok) {
        const jsonBody = await response.json();
        const jsonData = jsonBody.data;
        setLogFileArray((prevLogFileArray) =>
          prevLogFileArray.map((logFile) => {
            const eventsData = jsonData.filter((event) => event.log_id === logFile.id);
            return { ...logFile, eventsData, eventsLoading: false };
          })
        );
        setEventsLoading(false);
      } else {
        console.error(`Failed to fetch events`);
        setEventsLoading(true);
      }
    } catch (error) {
      console.error(`Error fetching events`, error);
      setEventsLoading(true);
    }
  };

  const toggleRowSelection = (logId) => {
    setSelectedLogs((prevSelectedLogs) =>
      prevSelectedLogs.includes(logId)
        ? prevSelectedLogs.filter((id) => id !== logId)
        : [...prevSelectedLogs, logId]
    );
  };

  const selectAllRows = (e) => {
    if (e.target.checked) {
      setSelectedLogs(LogFileArray.map((log) => log.id));
    } else {
      setSelectedLogs([]);
    }
  };

  const handleBulkDelete = async () => {
    const confirmDelete = window.confirm("Are you sure you want to delete the selected logs?");
    if (!confirmDelete) return;
    try {
      const response = await fetch("https://sucmhxbjlk.execute-api.us-east-1.amazonaws.com/prod/logs/delete/batch", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ logIds: selectedLogs }),
      });

      if (response.ok) {
        const updatedLogFileArray = LogFileArray.filter((log) => !selectedLogs.includes(log.id));
        setLogFileArray(updatedLogFileArray);
        setSelectedLogs([]);
      } else {
        alert("Failed to delete selected logs.");
      }
    } catch (error) {
      console.error("Error deleting selected logs:", error);
    }
  };

  const handleViewDataClick = () => {
    const selectedLogFiles = LogFileArray.filter((log) => selectedLogs.includes(log.id));
    navigate("/logs/data/" + product.id, { state: { product, selectedLogFiles } });
  };

  const handleViewChartClick = () => {
    const selectedLogFiles = LogFileArray.filter((log) => selectedLogs.includes(log.id));
    navigate("/logs/chart/" + product.id, { state: { product, selectedLogFiles } });
  };

  const handleAddLogClick = (product) => {
    navigate("/logs/add/" + product.id, { state: { product } });
  };

  const handleEditLogNoteClick = (product) => { };

  const handleDeleteLogClick = async (logId) => {
    try {
      const response = await fetch(`https://sucmhxbjlk.execute-api.us-east-1.amazonaws.com/prod/logs/delete/${logId}`);
      if (response.ok) {
        const updatedLogFileArray = LogFileArray.filter((log) => log.id !== logId);
        setLogFileArray(updatedLogFileArray);
      } else {
        console.error("Failed to delete log file");
      }
    } catch (error) {
      console.error("Error deleting log file:", error);
    }
  };

  const handleShowMoreEvents = (logId) => {
    setExpandedLogs((currentExpandedLogs) =>
      currentExpandedLogs.includes(logId)
        ? currentExpandedLogs.filter((id) => id !== logId)
        : [...currentExpandedLogs, logId]
    );
  };

  const handleNavigateHome = () => {
    navigate("/product");
  };

  return (
    <div className="logHomeContainer">
      <div className="logHomeHeader">
        {LogFilesLoading ? (
          <p>Loading...</p>
        ) : (
          <div className="logHomeHeaderTitle">
            <div className="logHomeProductType">{product.product_type}</div>
            <div className="logHomeProductSerialNumber">{product.product_serial_number}</div>
          </div>
        )}
        <div className="logHomeHeaderSearch">
          <div className="logHomeHeaderSearchIcon">
            <FontAwesomeIcon icon={faSearch} className="searchIcon" />
          </div>
          <div className="logHomeHeaderSearchInput">
            <input type="text" placeholder="Search/Filter..." />
          </div>
        </div>
        <div className="logHomeFunctionButtons">
          <div className="logHomeNavigateHome">
            <button className="navigationButton" onClick={() => handleNavigateHome()}>
              <FontAwesomeIcon icon={faHouse} /> My Products
            </button>
          </div>
          {selectedLogs.length > 0 && (
            <>
              <div className="logHomeHeaderOperation">
                <button onClick={handleViewDataClick}>
                  <FontAwesomeIcon icon={faTable} /> View Data
                </button>
              </div>
              <div className="logHomeHeaderOperation">
                <button onClick={handleViewChartClick}>
                  <FontAwesomeIcon icon={faChartLine} /> View Chart
                </button>
              </div>
              <div className="logHomeHeaderOperation">
                <button onClick={handleBulkDelete}>
                  <FontAwesomeIcon icon={faTrash} /> Delete Selected
                </button>
              </div>
            </>
          )}
          <div className="logHomeHeaderOperation">
            <button onClick={() => handleAddLogClick(product)}>
              <FontAwesomeIcon icon={faPlus} /> Add Log File(s)
            </button>
          </div>
        </div>
      </div>
      {LogFilesLoading ? (
        <p>Loading...</p>
      ) : (
        <div className="logHomeTableContent">
          <div className="logHomeFilterContent">
            <div className="logHomeFilterSelection">
              <Select
                closeMenuOnSelect={false}
                options={groupedOptions}
                isMulti
                formatGroupLabel={formatGroupLabel}
                styles={colorStyles}
              />
            </div>
            <button className="logHomeFilterButton">
              <FontAwesomeIcon icon={faSearch} className="searchIcon" /> Search
            </button>
          </div>
          <div className="logHomeTableScroll">
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      onChange={selectAllRows}
                      checked={
                        LogFileArray.length > 0 && selectedLogs.length === LogFileArray.length
                      }
                    />
                  </th>
                  <th>Log File</th>
                  <th>Date Uploaded</th>
                  <th>{EventsLoading ? "Events (loading)" : "Events"}</th>
                  <th>Notes</th>
                  <th>Operations</th>
                </tr>
              </thead>
              <tbody>
                {LogFileArray.length > 0 ? (
                  LogFileArray.map((item, index) => (
                    <tr key={index}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedLogs.includes(item.id)}
                          onChange={() => toggleRowSelection(item.id)}
                        />
                      </td>
                      <td>{item.log_filename}</td>
                      <td>{item.log_upload_date}</td>
                      <td>
                        {item.eventsData
                          .slice(0, expandedLogs.includes(item.id) ? item.eventsData.length : 5)
                          .map((event, eventIndex) => (
                            <div key={eventIndex}>{event.date} {event.time} {event.event}</div>
                          ))}
                        {item.eventsData.length > 5 && (
                          <button className="moreButton" onClick={() => handleShowMoreEvents(item.id)}>
                            {expandedLogs.includes(item.id) ? "Less..." : "More..."}
                          </button>
                        )}
                      </td>
                      <td></td>
                      <td>
                        <div className="buttonCell">
                          <button className="button" onClick={() => handleEditLogNoteClick(product)}>
                            <FontAwesomeIcon icon={faPenToSquare} /> Edit Note
                          </button>
                          <button className="button" onClick={() => handleDeleteLogClick(item.id)}>
                            <FontAwesomeIcon icon={faTrash} /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6">No data available</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
};

export default LogsHome;
